/**
 * Resource model for the incremental economy.
 *
 * `Minerals` is the Level-1 currency (mined from asteroids). Later tiers unlock
 * `Alloy` (refined), `Plasma` (combat/ammo), and the prestige currency
 * `DarkMatter` (earned only via wormhole jumps). Keeping them in one typed
 * record makes save/serialise and HUD binding trivial.
 */
export type ResourceType =
  | "minerals"
  | "alloy"
  | "plasma"
  | "darkMatter"
  | "units"; // Universal Units — traded for minerals at trade-hub planets

export type ResourceBag = Record<ResourceType, number>;

export function emptyBag(): ResourceBag {
  return { minerals: 0, alloy: 0, plasma: 0, darkMatter: 0, units: 0 };
}

export class ResourceStore {
  readonly bag: ResourceBag = emptyBag();

  add(type: ResourceType, amount: number): void {
    this.bag[type] += amount;
  }

  /** Attempt to spend; returns false and changes nothing if unaffordable. */
  spend(cost: Partial<ResourceBag>): boolean {
    for (const key in cost) {
      const k = key as ResourceType;
      if (this.bag[k] < (cost[k] ?? 0)) return false;
    }
    for (const key in cost) {
      const k = key as ResourceType;
      this.bag[k] -= cost[k] ?? 0;
    }
    return true;
  }

  can(cost: Partial<ResourceBag>): boolean {
    for (const key in cost) {
      const k = key as ResourceType;
      if (this.bag[k] < (cost[k] ?? 0)) return false;
    }
    return true;
  }
}
